import { createToggler } from '@leprecoins/effector';
import { clamp } from '@mantine/hooks';
import { createEvent, createStore, merge, sample } from 'effector';
import { and, equals, not, or } from 'patronum';
import { gameModel, ONBOARDING_STEPS } from 'entities/game';
import {
	receiveBet,
	receiveTurn,
	TURN_STATUSES,
	sendTurn,
	TURN_TYPES,
	sendBet,
	receiveRefresh,
} from 'shared/api';
import { breakpoints } from 'shared/lib';

const bettingToggler = createToggler(true);

const wagerFieldToggler = createToggler(true);

const betButtonToggler = createToggler(true);

const cashoutButtonToggler = createToggler(true);

const $wager = gameModel.$wager.map(Number);

const $profit = createStore(0);

const $enableOnboardingBetMenu = and(
	gameModel.$isOnboarding,
	equals(gameModel.$onboardingStep, ONBOARDING_STEPS.BET_MENU),
);

const $enableOnboardingBetButton = and(
	gameModel.$isOnboarding,
	equals(gameModel.$onboardingStep, ONBOARDING_STEPS.BET_BUTTON),
);

const $enableOnboardingWagerField = and(
	gameModel.$isOnboarding,
	equals(gameModel.$onboardingStep, ONBOARDING_STEPS.WAGER_FIELD),
);

const $betting = bettingToggler.$active;

const $playing = not(bettingToggler.$active);

const $wagerFieldDisabled = not(wagerFieldToggler.$active);

const $wagerFieldReadOnly = not(breakpoints.sm.$matches);

const $betButtonDisabled = or(not(betButtonToggler.$active), gameModel.$broke);

const $cashoutButtonDisabled = not(cashoutButtonToggler.$active);

const wagerChanged = createEvent<string>();

const betFormSubmitted = createEvent();

const clearBetButtonClicked = createEvent();

const doubleBetButtonClicked = createEvent();

const fastBetButtonClicked = createEvent<number>();

const cashoutButtonClicked = createEvent();

const betSuccessReceived = receiveBet.success.filterMap(({ status, balance }) =>
	status ? balance : undefined,
);

const betFailureReceived = merge([
	receiveBet.success.filterMap(({ status }) => !status || undefined),
	receiveBet.failure,
]);

const turnBetReceived = receiveTurn.success.filterMap(({ status, profit }) =>
	status === TURN_STATUSES.bet ? profit : undefined,
);

const turnWinReceived = receiveTurn.success.filterMap(({ status, profit }) =>
	status === TURN_STATUSES.win ? profit : undefined,
);

const turnLoseReceived = receiveTurn.success.filterMap(({ status, profit }) =>
	status === TURN_STATUSES.lose ? profit : undefined,
);

const sendTurnFinish = sendTurn.prepend(() => ({ type: TURN_TYPES.finish }));

const betRefreshReceived = sample({
	clock: receiveRefresh.success,
	source: gameModel.$balance,
	filter: (_, { status, turnHistory }) => status === TURN_STATUSES.bet && turnHistory.length === 0,
});

const turnRefreshReceived = sample({
	clock: receiveRefresh.success,
	filter: ({ status, turnHistory }) => status === TURN_STATUSES.bet && turnHistory.length !== 0,
	fn: ({ profit }) => profit,
});

sample({
	clock: wagerChanged,
	target: gameModel.$wager,
});

sample({
	clock: betFormSubmitted,
	source: {
		wager: $wager,
		clientSeed: gameModel.$clientSeed,
	},
	filter: not(gameModel.$broke),
	target: sendBet,
});

sample({
	clock: sendBet,
	target: [
		bettingToggler.enable,
		wagerFieldToggler.disable,
		betButtonToggler.disable,
		cashoutButtonToggler.disable,
	],
});

sample({
	clock: [betSuccessReceived, betRefreshReceived],
	target: [
		gameModel.$balance,
		bettingToggler.disable,
		wagerFieldToggler.disable,
		betButtonToggler.disable,
		cashoutButtonToggler.disable,
	],
});

sample({
	clock: [betFailureReceived, gameModel.receiveInsufficientBalance],
	target: [
		bettingToggler.enable,
		wagerFieldToggler.enable,
		betButtonToggler.enable,
		cashoutButtonToggler.disable,
	],
});

sample({
	clock: sendTurn,
	target: [
		bettingToggler.disable,
		wagerFieldToggler.disable,
		betButtonToggler.disable,
		cashoutButtonToggler.disable,
	],
});

sample({
	clock: [turnBetReceived, turnRefreshReceived],
	target: [
		$profit,
		bettingToggler.disable,
		wagerFieldToggler.disable,
		betButtonToggler.disable,
		cashoutButtonToggler.enable,
	],
});

sample({
	clock: [turnLoseReceived, turnWinReceived],
	target: [
		$profit.reinit,
		bettingToggler.enable,
		wagerFieldToggler.enable,
		betButtonToggler.enable,
		cashoutButtonToggler.disable,
	],
});

sample({
	clock: cashoutButtonClicked,
	target: sendTurnFinish,
});

sample({
	clock: clearBetButtonClicked,
	source: gameModel.$minBet,
	fn: String,
	target: gameModel.$wager,
});

sample({
	clock: doubleBetButtonClicked,
	source: [$wager, gameModel.$maxBet],
	fn: ([wager, maxBet]) => `${Math.min(wager * 2, maxBet)}`,
	target: gameModel.$wager,
});

sample({
	clock: fastBetButtonClicked,
	source: [gameModel.$minBet, gameModel.$maxBet],
	fn: ([minBet, maxBet], fastBet) => `${clamp(fastBet, minBet, maxBet)}`,
	target: gameModel.$wager,
});

export const betMenuModel = {
	$profit,
	$betting,
	$playing,
	$wagerFieldDisabled,
	$wagerFieldReadOnly,
	$betButtonDisabled,
	$cashoutButtonDisabled,
	wagerChanged,
	betFormSubmitted,
	clearBetButtonClicked,
	doubleBetButtonClicked,
	fastBetButtonClicked,
	cashoutButtonClicked,
	$enableOnboardingBetMenu,
	$enableOnboardingBetButton,
	$enableOnboardingWagerField,
};
